import { useIsMobile } from '@/hooks/useIsMobile'
import type { YardiGuestCardOccupantsPanelProps, YardiGuestCardRow } from '../types'
import { GUEST_CARD_OCCUPANT_COL_WIDTHS_PCT } from '../constants'
import { guestCardOccupantName } from '../utils/yardiGuestCardGrouping'
import { maskformatPhoneNumber, sanitizeDataTestIdSegment } from '../utils/yardiInviteUi'
import yardiTableStyles from '../styles/yardiListing.module.css'

function OccupantMobileField({
  label,
  value,
  testId,
}: {
  label: string
  value: string
  testId: string
}) {
  return (
    <div className={yardiTableStyles.guestSearchOccupantMobileField} data-testid={testId}>
      <span className={yardiTableStyles.guestSearchOccupantMobileLabel}>{label}</span>
      <span className={yardiTableStyles.guestSearchOccupantMobileValue}>{value || '—'}</span>
    </div>
  )
}

function OccupantMobileCards({
  applicants,
  rowSuffix,
}: {
  applicants: YardiGuestCardRow[]
  rowSuffix: string
}) {
  return (
    <div
      className={yardiTableStyles.guestSearchOccupantMobileList}
      data-testid={`yardi-guest-search-applicant-panel-mobile-list-${rowSuffix}`}
    >
      {applicants.map((applicant, i) => {
        const occupantKey = `${sanitizeDataTestIdSegment(String(applicant.prospectId ?? 'row'))}-${i}`
        return (
          <div
            key={`${applicant.prospectId}-${i}`}
            className={yardiTableStyles.guestSearchOccupantMobileCard}
            data-testid={`yardi-guest-search-applicant-panel-mobile-card-${rowSuffix}-${occupantKey}`}
          >
            <OccupantMobileField
              label="Prospect ID"
              value={String(applicant.prospectId ?? '')}
              testId={`yardi-guest-search-applicant-panel-mobile-prospect-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Tenant ID"
              value={String(applicant.tenantId ?? '')}
              testId={`yardi-guest-search-applicant-panel-mobile-tenant-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Name"
              value={guestCardOccupantName(applicant)}
              testId={`yardi-guest-search-applicant-panel-mobile-name-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Email"
              value={String(applicant.email ?? '')}
              testId={`yardi-guest-search-applicant-panel-mobile-email-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Phone"
              value={applicant.mobileNo ? maskformatPhoneNumber(String(applicant.mobileNo)) : ''}
              testId={`yardi-guest-search-applicant-panel-mobile-phone-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Relationship Type"
              value={String(applicant.type ?? '')}
              testId={`yardi-guest-search-applicant-panel-mobile-relationship-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Manual"
              value={applicant.isManualInvite ? 'Yes' : 'No'}
              testId={`yardi-guest-search-applicant-panel-mobile-manual-${rowSuffix}-${occupantKey}`}
            />
            <OccupantMobileField
              label="Automated"
              value={applicant.isAutomatedInvite ? 'Yes' : 'No'}
              testId={`yardi-guest-search-applicant-panel-mobile-automated-${rowSuffix}-${occupantKey}`}
            />
          </div>
        )
      })}
    </div>
  )
}

function OccupantDesktopTable({
  applicants,
  rowSuffix,
}: {
  applicants: YardiGuestCardRow[]
  rowSuffix: string
}) {
  return (
    <div
      className={yardiTableStyles.guestSearchOccupantTableWrap}
      data-testid={`yardi-guest-search-applicant-panel-table-wrap-${rowSuffix}`}
    >
      <table
        className={yardiTableStyles.guestSearchOccupantTable}
        data-testid={`yardi-guest-search-applicant-panel-table-${rowSuffix}`}
      >
        <colgroup>
          {GUEST_CARD_OCCUPANT_COL_WIDTHS_PCT.map((pct, i) => (
            <col key={i} style={{ width: `${pct}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr data-testid={`yardi-guest-search-applicant-panel-table-head-${rowSuffix}`}>
            <th scope="col">Prospect ID</th>
            <th scope="col">Tenant ID</th>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Phone</th>
            <th scope="col">Relationship Type</th>
            <th scope="col">Manual</th>
            <th scope="col">Automated</th>
          </tr>
        </thead>
        <tbody data-testid={`yardi-guest-search-applicant-panel-table-body-${rowSuffix}`}>
          {applicants.map((applicant, i) => {
            const occupantKey = `${sanitizeDataTestIdSegment(String(applicant.prospectId ?? 'row'))}-${i}`
            return (
              <tr
                key={`${applicant.prospectId}-${i}`}
                data-testid={`yardi-guest-search-applicant-panel-row-${rowSuffix}-${occupantKey}`}
              >
                <td>{applicant.prospectId}</td>
                <td>{applicant.tenantId}</td>
                <td>{guestCardOccupantName(applicant)}</td>
                <td>{applicant.email}</td>
                <td>{applicant.mobileNo ? maskformatPhoneNumber(String(applicant.mobileNo)) : ''}</td>
                <td>{applicant.type}</td>
                <td>{applicant.isManualInvite ? 'Yes' : 'No'}</td>
                <td>{applicant.isAutomatedInvite ? 'Yes' : 'No'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function YardiGuestCardOccupantsPanel({
  applicants,
  rowSuffix,
}: YardiGuestCardOccupantsPanelProps) {
  const isMobile = useIsMobile()

  if (!applicants.length) {
    return (
      <div
        className={yardiTableStyles.guestSearchOccupantPanel}
        data-testid={`yardi-guest-search-applicant-panel-empty-${rowSuffix}`}
      >
        <h6 className={yardiTableStyles.guestSearchOccupantPanelHeading}>No applicants found</h6>
      </div>
    )
  }

  return (
    <div
      className={yardiTableStyles.guestSearchOccupantPanel}
      data-testid={`yardi-guest-search-applicant-panel-${rowSuffix}`}
    >
      <h6
        className={yardiTableStyles.guestSearchOccupantPanelHeading}
        data-testid={`yardi-guest-search-applicant-panel-heading-${rowSuffix}`}
      >
        Occupants
      </h6>
      {isMobile ? (
        <OccupantMobileCards applicants={applicants} rowSuffix={rowSuffix} />
      ) : (
        <OccupantDesktopTable applicants={applicants} rowSuffix={rowSuffix} />
      )}
    </div>
  )
}
